/**
 * TrpcMyDataExportAdapter — the production `MyDataExportPort` over the typed
 * tRPC client (033 US5, T041). Calls the live
 * `member.myData.export({ format: 'json' })`: the member's cookie session
 * authenticates the same-origin request and `memberProcedure` on the API side
 * guarantees only a member may export, and only their own data (the API
 * derives the subject from the session, never the payload).
 *
 * The `member` namespace is OPTIONAL on the published `AppRouter` (the
 * conditional-spread posture — production mounts it, some test compositions
 * omit it), so the adapter narrows before calling and fails loudly if wired
 * against a router without the namespace (a composition error, not a runtime
 * condition — loud > silent, P7). Mirrors `TrpcAccountDeletionAdapter`.
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 */

import type { WebTrpcClient } from '../trpc/client.js';
import {
  EXPORT_FORMAT,
  type MyDataExportPort,
  type MyDataExportResult,
} from './MyDataExportPort.js';

export class TrpcMyDataExportAdapter implements MyDataExportPort {
  readonly #client: WebTrpcClient;

  constructor(client: WebTrpcClient) {
    this.#client = client;
  }

  async requestExport(): Promise<MyDataExportResult> {
    const member = this.#client.member;
    // Both `member` and `member.myData` are optional on the published router
    // (each member sub-surface mounts independently). Absence is a composition
    // error, not a runtime condition — loud > silent (P7).
    if (member?.myData === undefined) {
      throw new Error(
        'TrpcMyDataExportAdapter: member.myData is absent on the AppRouter — ' +
          'data export requires the 033 US5 API activation.',
      );
    }
    // No error mapping: the API has no export-specific refusal code (unlike
    // erasure's PRECONDITION_FAILED on unconfigured retention — the export
    // needs no retention parameter). Every failure rethrows unchanged and the
    // UI renders its retryable error state.
    return await member.myData.export.mutate({ format: EXPORT_FORMAT });
  }
}
